class CartNotification extends HTMLElement {
  constructor() {
    super();

    this.notification = document.getElementById('cart-notification');
    this.wrapper = this.querySelector('.cart-notification-wrapper');
    this.header = document.querySelector('sticky-header');
    this.onBodyClick = this.handleBodyClick.bind(this);
    this.onOverlayClick = this.handleOverlayClick.bind(this);

    this.notification.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    
    // Only attach close handler to the actual close button and continue shopping button
    const closeButton = this.querySelector('.cart-notification__close');
    if (closeButton) {
      closeButton.addEventListener('click', this.close.bind(this));
    }
    
    const continueButton = this.querySelector('.link.button-label');
    if (continueButton) {
      continueButton.addEventListener('click', this.close.bind(this));
    }
  }

  open() {
    // Add active class to wrapper for overlay
    this.wrapper.classList.add('active');
    
    // Add classes to notification with slight delay for smooth animation
    setTimeout(() => {
      this.notification.classList.add('animate', 'active');
    }, 10);

    this.notification.addEventListener(
      'transitionend',
      () => {
        this.notification.focus();
        trapFocus(this.notification);
      },
      { once: true }
    );

    // Add overlay click listener
    this.wrapper.addEventListener('click', this.onOverlayClick);
    
    // Prevent body scroll when drawer is open
    document.body.style.overflow = 'hidden';
  }

  close() {
    this.notification.classList.remove('active');
    this.wrapper.classList.remove('active');
    
    // Re-enable body scroll
    document.body.style.overflow = '';

    removeTrapFocus(this.activeElement);
    
    // Remove overlay click listener
    this.wrapper.removeEventListener('click', this.onOverlayClick);
  }

  handleOverlayClick(evt) {
    // Close if clicking on the overlay (wrapper) but not the notification itself
    if (evt.target === this.wrapper) {
      this.close();
    }
  }

  renderContents(parsedState) {
    this.cartItemKey = parsedState.key;
    this.getSectionsToRender().forEach((section) => {
      document.getElementById(section.id).innerHTML = this.getSectionInnerHTML(
        parsedState.sections[section.id],
        section.selector
      );
    });

    if (this.header) this.header.reveal();
    this.open();
    
    // Initialize cart functionality after rendering with a slight delay to ensure DOM is ready
    setTimeout(() => {
      this.initializeCartControls();
    }, 100);
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-notification-message',
      },
      {
        id: 'cart-notification-product',
      },
      {
        id: 'cart-notification-button',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  handleBodyClick(evt) {
    const target = evt.target;
    if (target !== this.notification && !target.closest('cart-notification')) {
      const disclosure = target.closest('details-disclosure, header-menu');
      this.activeElement = disclosure ? disclosure.querySelector('summary') : null;
      this.close();
    }
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  initializeCartControls() {
    console.log('Initializing cart controls...');
    
    // Remove existing delegated listeners if any
    if (this.cartControlsHandler) {
      this.notification.removeEventListener('click', this.cartControlsHandler);
    }
    
    // Use event delegation for all cart controls
    this.cartControlsHandler = (e) => {
      const target = e.target;
      const button = target.closest('[data-quantity-change], [data-remove-item]');
      
      if (!button) return;
      
      e.preventDefault();
      e.stopPropagation();
      
      const cartItem = button.closest('[data-cart-item]');
      if (!cartItem) {
        console.error('Cart item not found for button:', button);
        return;
      }
      
      // Handle quantity change
      if (button.hasAttribute('data-quantity-change')) {
        console.log('Quantity button clicked');
        const change = parseInt(button.dataset.quantityChange);
        const input = cartItem.querySelector('[data-quantity-input]');
        if (input) {
          const newQuantity = Math.max(1, parseInt(input.value) + change);
          this.updateQuantity(cartItem, newQuantity);
        }
      }
      
      // Handle remove item
      if (button.hasAttribute('data-remove-item')) {
        console.log('Remove button clicked');
        this.removeItem(cartItem);
      }
    };
    
    this.notification.addEventListener('click', this.cartControlsHandler);
    
    // Handle quantity input changes
    const quantityInputs = this.notification.querySelectorAll('[data-quantity-input]');
    console.log('Found quantity inputs:', quantityInputs.length);
    
    quantityInputs.forEach(input => {
      input.addEventListener('change', (e) => {
        const cartItem = input.closest('[data-cart-item]');
        if (cartItem) {
          const newQuantity = Math.max(1, parseInt(input.value) || 1);
          input.value = newQuantity;
          this.updateQuantity(cartItem, newQuantity);
        }
      });

      input.addEventListener('keypress', (e) => {
        if (e.which < 48 || e.which > 57) {
          e.preventDefault();
        }
      });
    });
  }

  async updateQuantity(cartItem, quantity) {
    const line = parseInt(cartItem.dataset.line);
    const input = cartItem.querySelector('[data-quantity-input]');
    
    cartItem.classList.add('updating');
    
    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          line: line,
          quantity: quantity
        })
      });

      if (!response.ok) {
        throw new Error('Failed to update cart');
      }

      const cart = await response.json();
      
      // Update all cart sections
      await this.refreshCartSections();
      
      input.value = quantity;
      
    } catch (error) {
      console.error('Error updating quantity:', error);
      input.value = input.defaultValue;
    } finally {
      cartItem.classList.remove('updating');
    }
  }

  async removeItem(cartItem) {
    const line = parseInt(cartItem.dataset.line);
    
    cartItem.classList.add('updating');
    
    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          line: line,
          quantity: 0
        })
      });

      if (!response.ok) {
        throw new Error('Failed to remove item');
      }

      const cart = await response.json();
      
      // Remove item from DOM with animation
      cartItem.style.transition = 'opacity 0.3s, transform 0.3s';
      cartItem.style.opacity = '0';
      cartItem.style.transform = 'translateX(-20px)';
      
      setTimeout(async () => {
        cartItem.remove();
        
        // If cart is empty, close the drawer
        if (cart.item_count === 0) {
          this.close();
        } else {
          // Refresh cart sections
          await this.refreshCartSections();
        }
      }, 300);
      
    } catch (error) {
      console.error('Error removing item:', error);
      cartItem.classList.remove('updating');
    }
  }

  async refreshCartSections() {
    try {
      const response = await fetch(`${window.Shopify.routes.root}?sections=cart-notification-message,cart-notification-product,cart-notification-button,cart-icon-bubble`);
      const sections = await response.json();
      
      // Update message section
      const messageElement = document.getElementById('cart-notification-message');
      if (messageElement && sections['cart-notification-message']) {
        messageElement.innerHTML = this.getSectionInnerHTML(sections['cart-notification-message'], '.shopify-section');
      }
      
      // Update button/total section
      const buttonElement = document.getElementById('cart-notification-button');
      if (buttonElement && sections['cart-notification-button']) {
        buttonElement.innerHTML = this.getSectionInnerHTML(sections['cart-notification-button'], '.shopify-section');
      }
      
      // Update cart icon bubble
      const bubbleElement = document.getElementById('cart-icon-bubble');
      if (bubbleElement && sections['cart-icon-bubble']) {
        bubbleElement.innerHTML = this.getSectionInnerHTML(sections['cart-icon-bubble'], '.shopify-section');
      }
      
      // Reinitialize controls
      this.initializeCartControls();
      
    } catch (error) {
      console.error('Error refreshing cart sections:', error);
    }
  }
}

customElements.define('cart-notification', CartNotification);
